/**
 * Template tokens are `{{key}}`, or `{{key|fallback}}` when the author set text to show in place of a
 * missing value. The fallback is everything after the pipe. It cannot contain `{`, `}` or `|`, which
 * keeps the token parseable, or `<` / `>`, so a fallback can never carry markup into the rendered
 * HTML — a hand-edited `{{key|<img …>}}` is not a token at all (see `sanitizeFallback`). The
 * editor stores a typed `<` as `&lt;`, so real fallbacks lose nothing.
 *
 * This is the single definition of the token grammar — every place that reads or writes tokens
 * (`template()`, the editor's chip round trip) goes through it so the two can never disagree.
 */
// eslint-disable-next-line security/detect-unsafe-regex -- linear: the optional group holds one negated class, nothing nested
export const TEMPLATE_TOKEN_REGEX = /\{\{(\w+)(?:\|([^{}|<>]*))?\}\}/g

export interface TemplateToken {
  key: string
  /** Empty when the token has no fallback. */
  fallback: string
}

/** Strips the characters the token grammar does not allow. */
export function sanitizeFallback(fallback?: string | null): string {
  return (fallback ?? '').replaceAll(/[{}|<>]/g, '')
}

export function buildTemplateToken(key: string, fallback?: string | null): string {
  const clean = sanitizeFallback(fallback)
  return clean ? `{{${key}|${clean}}}` : `{{${key}}}`
}

/** Rewrites every token in `text` with `replacer`. */
export function replaceTemplateTokens(text: string, replacer: (token: TemplateToken, match: string) => string): string {
  if (!text) return text
  // Safe to share the global regex: replaceAll resets `lastIndex` before it starts.
  return text.replaceAll(TEMPLATE_TOKEN_REGEX, (match, key: string, fallback?: string) =>
    replacer({ key, fallback: fallback ?? '' }, match)
  )
}

/**
 * The fallback pre-filled when an author picks a placeholder, so the common greetings read well when
 * the estimate is missing the value ("Hi there" rather than "Hi ").
 */
export const DEFAULT_PLACEHOLDER_FALLBACKS: Readonly<Record<string, string>> = {
  clientName: 'there',
  clientFirstName: 'there',
  address: 'your property',
  estimatorName: 'our team',
  estimatorFirstName: 'our team'
}

export function getDefaultFallback(key?: string | null): string {
  return (key && DEFAULT_PLACEHOLDER_FALLBACKS[key]) || ''
}
